// ═══════════════════════════════════════════════════════════════════
// FieldAtmos3D.js — SHIP 7: THE ATMOSPHERE
// ═══════════════════════════════════════════════════════════════════
// The Shatterfield stops being a table with cards on it and becomes a
// PLACE: Anima bleeds out of a crystal well and pools as crimson mist,
// Khao runs the perimeter in rivers of violet light and falls into a
// funnel, the seam is a lit tear in the world, and the whole duel sits
// inside a horizon of aenergy that takes sides.
//
// THE CONTRACT (same as every file in the 3D stack):
//   • PURE RENDERER. Zero game logic, zero Firebase, zero handlers, zero
//     click targets. It reads four numbers and time, and it draws.
//   • It cannot break the duel. Delete the one <FieldAtmos3D/> line in
//     Shatterfield3D and the board renders exactly as Ship 6 shipped it.
//
// THE LAW OF THIS SHIP — **THE ATMOSPHERE FRAMES THE BOARD, NEVER
// COVERS IT.** Every mist bank, river lane and well lives on the
// PERIMETER — outside the rift rows, outside the hand, outside the
// piles. A duel you can't read is a pretty screensaver. The one time
// the mist is allowed to creep inward is when your Anima is nearly
// gone, and at that point it is telling you the truth.
//
// PERF LAW (ie-realtime-perf): NO setState anywhere in this file. Every
// animation is imperative on a ref inside useFrame. Every prop-driven
// value flows through the module-level ATMOS bus (React context does
// NOT cross the <Canvas> boundary — the CAM_STATE pattern). Every
// component below is memo'd and takes only stable primitives, so a
// prop change on the wrapper reconciles NOTHING beneath it.
//
// PRNG LAW: mulberry32 seeded by a string key. Never Math.random() in a
// render path — the mist is identical on every client, forever.
//
// PALETTE LAW: SD only. This is the field, not the chrome.
// ═══════════════════════════════════════════════════════════════════
import { memo, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { SD } from "./theme";
import { TABLE, Y } from "./fieldLayout3D";
import { MAX_ANIMA } from "./rules";

// ═══════════════════════════════════════════════════════════════════
// THE ATMOS BUS — one writer (the wrapper's effect), many readers
// (every useFrame below). Zero re-renders, one source of truth.
// ═══════════════════════════════════════════════════════════════════
export const ATMOS = {
  maxAnima: MAX_ANIMA,
  myAnima: 30, oppAnima: 30,
  myKhao: 3, myMaxKhao: 3,
  oppKhao: 3, oppMaxKhao: 3,
  myTurn: true,
  turnAt: 0,     // timestamp of the last chair change (horizon pulse)
  strikeAt: 0,   // timestamp of the last Anima loss (seam flare)

  // ── H24 · THE SURGE ──────────────────────────────────────────────
  // A score may wash the WHOLE FIELD in its own colour. Until now the
  // atmosphere only ever reacted to the two things it could read off
  // its own props — Anima moving and the chair changing — so the most
  // consequential moments in a duel (a Signature resolving, a Soul
  // being cut, the Solrend ending) happened entirely inside the cue
  // layer while the world around them stayed exactly as calm as it had
  // been a second earlier.
  //
  // This is the seam that fixes it, and it is deliberately the same
  // shape as `strikeAt`: ONE writer, a timestamp, and readers that
  // decay off it. No new subscription, no state, no re-render — the
  // atmosphere still reconciles ZERO on a Firebase tick.
  //
  //   surgeAt   when it fired
  //   surgeMs   how long it takes to die
  //   surgeMag  0–1, how much of the field it takes
  //   surgeHue  a THREE.Color-parseable hex; the score's own colour
  surgeAt: 0, surgeMs: 900, surgeMag: 0, surgeHue: "#e8b84b",
};

/**
 * Fire a surge. Called by the renderer when a score declares one.
 * Last writer wins — two simultaneous surges must not add up into
 * something that looks like a bug, and the more recent event is always
 * the more interesting one.
 */
export function surge(hue, mag, ms) {
  ATMOS.surgeAt = Date.now();
  ATMOS.surgeHue = hue || "#e8b84b";
  ATMOS.surgeMag = Math.max(0, Math.min(1, mag == null ? 0.6 : mag));
  ATMOS.surgeMs = Math.max(120, ms || 900);
}

/** 0 → 1 → 0. How much surge is live right now. Quadratic decay, so it
 *  arrives hard and leaves gently — a flash, not a fade. */
export function surgeLevel() {
  if (!ATMOS.surgeMag) return 0;
  const k = (Date.now() - ATMOS.surgeAt) / ATMOS.surgeMs;
  if (k < 0 || k >= 1) return 0;
  const rise = k < 0.12 ? k / 0.12 : 1;
  const fall = 1 - (k - 0.12) / 0.88;
  return ATMOS.surgeMag * rise * fall * fall;
}

// ── THE BUDGET ─────────────────────────────────────────────────────
// One flag, read once at module load, that thins the ship on a phone
// or a weak core count. It never removes an EFFECT — every layer still
// renders — it only drops the two extra lights per seat and trims the
// particle counts. Nothing is made ugly to make it fast (perf law); the
// mist, the rivers, the wells and the horizon are all still there.
const LOW = typeof window !== "undefined" && (
  window.innerWidth < 900 ||
  ((typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 8) <= 4
);

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
// How badly is this seat bleeding? 0 = untouched, 1 = one hit from gone.
const woundOf = (isMe) =>
  clamp01(1 - (isMe ? ATMOS.myAnima : ATMOS.oppAnima) / Math.max(1, ATMOS.maxAnima));
const khaoOf = (isMe) => {
  const c = isMe ? ATMOS.myKhao : ATMOS.oppKhao;
  const m = Math.max(1, isMe ? ATMOS.myMaxKhao : ATMOS.oppMaxKhao);
  return clamp01(c / m);
};

// ── mulberry32 — THE seeded PRNG (copied verbatim, per the law) ──
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const seedOf = (str) => {
  let h = 9;
  const s = String(str || "seed");
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 387420489);
  return h >>> 0;
};
const rnd = (key) => mulberry32((seedOf(key) * 2654435761) % 2147483647);

// Frame-rate-independent approach (never a raw *0.08 on a variable dt).
const approach = (cur, goal, rate, dt) => cur + (goal - cur) * (1 - Math.exp(-rate * dt));

// ═══════════════════════════════════════════════════════════════════
// PROCEDURAL TEXTURES — canvas → CanvasTexture, cached module-level by
// key. Every one of these is white/alpha only; colour comes from the
// material's `color`, so ONE texture serves both seats in two palettes.
// ═══════════════════════════════════════════════════════════════════
const A_TEX = new Map();
function makeTex(key, w, h, draw, opts = {}) {
  if (A_TEX.has(key)) return A_TEX.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h, rnd(key));
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  t.wrapS = opts.wrapS || THREE.ClampToEdgeWrapping;
  t.wrapT = opts.wrapT || THREE.ClampToEdgeWrapping;
  A_TEX.set(key, t);
  return t;
}
const REP = THREE.RepeatWrapping;

// Soft blob field, vignetted to zero at the rim so a rotating plane
// never shows a hard square edge. The workhorse of all the mist.
const mistTex = () => makeTex("atmos-mist", 256, 256, (g, w, h, R) => {
  g.clearRect(0, 0, w, h);
  for (let i = 0; i < 30; i++) {
    const x = R() * w, y = R() * h, r = 22 + R() * 74;
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `rgba(255,255,255,${(0.05 + R() * 0.10).toFixed(3)})`);
    gr.addColorStop(0.55, `rgba(255,255,255,${(0.02 + R() * 0.04).toFixed(3)})`);
    gr.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  g.globalCompositeOperation = "destination-out";
  const vg = g.createRadialGradient(w / 2, h / 2, w * 0.16, w / 2, h / 2, w * 0.5);
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0,1)");
  g.fillStyle = vg; g.fillRect(0, 0, w, h);
  g.globalCompositeOperation = "source-over";
});

// A rail of light with bright pulses riding it. Scrolls along U.
// Keyed per lane so each lane owns its own offset (a shared texture
// would lock every river to one speed — one object, four writers).
const riverTex = (key) => makeTex(`atmos-river|${key}`, 256, 16, (g, w, h, R) => {
  g.clearRect(0, 0, w, h);
  const base = g.createLinearGradient(0, 0, 0, h);
  base.addColorStop(0, "rgba(255,255,255,0)");
  base.addColorStop(0.5, "rgba(255,255,255,0.5)");
  base.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = base; g.fillRect(0, 0, w, h);
  for (let i = 0; i < 9; i++) {
    const x = R() * w, len = 14 + R() * 40;
    const lg = g.createLinearGradient(x, 0, x + len, 0);
    lg.addColorStop(0, "rgba(255,255,255,0)");
    lg.addColorStop(0.5, "rgba(255,255,255,0.9)");
    lg.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = lg; g.fillRect(x, 0, len, h);
  }
}, { wrapS: REP });

// Five-arm spiral with a hot core — the face of a well, seen from above.
const spiralTex = () => makeTex("atmos-spiral", 256, 256, (g, w, h) => {
  g.clearRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2;
  g.lineCap = "round";
  for (let arm = 0; arm < 5; arm++) {
    const trace = () => {
      g.beginPath();
      for (let i = 0; i <= 90; i++) {
        const t = i / 90;
        const a = arm * (Math.PI * 2 / 5) + t * 3.0;
        const r = 12 + t * 108;
        const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
    };
    trace(); g.strokeStyle = "rgba(255,255,255,0.34)"; g.lineWidth = 9; g.stroke();
    trace(); g.strokeStyle = "rgba(255,255,255,0.92)"; g.lineWidth = 2.2; g.stroke();
  }
  const cg = g.createRadialGradient(cx, cy, 0, cx, cy, 66);
  cg.addColorStop(0, "rgba(255,255,255,0.85)");
  cg.addColorStop(0.4, "rgba(255,255,255,0.14)");
  cg.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = cg; g.fillRect(0, 0, w, h);
  g.globalCompositeOperation = "destination-out";
  const vg = g.createRadialGradient(cx, cy, w * 0.30, cx, cy, w * 0.5);
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0,1)");
  g.fillStyle = vg; g.fillRect(0, 0, w, h);
  g.globalCompositeOperation = "source-over";
});

// Vertical striations — wraps both ways, so it dresses a funnel wall
// (scroll V = matter falling in) and a horizon curtain alike.
const veilTex = (key) => makeTex(`atmos-veil|${key}`, 128, 128, (g, w, h, R) => {
  g.clearRect(0, 0, w, h);
  for (let i = 0; i < 46; i++) {
    const x = R() * w, wd = 0.8 + R() * 5.5, a = 0.08 + R() * 0.36;
    const lg = g.createLinearGradient(0, 0, 0, h);
    lg.addColorStop(0, "rgba(255,255,255,0)");
    lg.addColorStop(0.30, `rgba(255,255,255,${a.toFixed(3)})`);
    lg.addColorStop(0.68, `rgba(255,255,255,${(a * 0.45).toFixed(3)})`);
    lg.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = lg; g.fillRect(x, 0, wd, h);
  }
}, { wrapS: REP, wrapT: REP });

// The seam: a jagged tear with a bloom around it.
const crackTex = () => makeTex("atmos-crack", 1024, 64, (g, w, h, R) => {
  g.clearRect(0, 0, w, h);
  const pts = [];
  for (let x = 0; x <= w; x += 22) pts.push([x, h / 2 + (R() - 0.5) * h * 0.5]);
  const trace = () => {
    g.beginPath(); g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
  };
  g.lineJoin = "round"; g.lineCap = "round";
  trace(); g.strokeStyle = "rgba(255,255,255,0.10)"; g.lineWidth = 26; g.stroke();
  trace(); g.strokeStyle = "rgba(255,255,255,0.22)"; g.lineWidth = 10; g.stroke();
  trace(); g.strokeStyle = "rgba(255,255,255,0.75)"; g.lineWidth = 3.2; g.stroke();
  trace(); g.strokeStyle = "rgba(255,255,255,1)"; g.lineWidth = 1.1; g.stroke();
  // splinters off the main tear
  for (let i = 2; i < pts.length - 2; i += 3) {
    const [x, y] = pts[i];
    const dy = (R() < 0.5 ? -1 : 1) * (6 + R() * 14);
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + (R() - 0.5) * 30, y + dy);
    g.strokeStyle = "rgba(255,255,255,0.45)"; g.lineWidth = 1.4; g.stroke();
  }
  // fade both ends so the tear dies into the void, not into a hard stop
  g.globalCompositeOperation = "destination-out";
  const eg = g.createLinearGradient(0, 0, w, 0);
  eg.addColorStop(0, "rgba(0,0,0,1)");
  eg.addColorStop(0.12, "rgba(0,0,0,0)");
  eg.addColorStop(0.88, "rgba(0,0,0,0)");
  eg.addColorStop(1, "rgba(0,0,0,1)");
  g.fillStyle = eg; g.fillRect(0, 0, w, h);
  g.globalCompositeOperation = "source-over";
});

// Soft round sprite — points look like cheap squares without one.
const sparkTex = () => makeTex("atmos-spark", 64, 64, (g, w, h) => {
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, "rgba(255,255,255,1)");
  gr.addColorStop(0.28, "rgba(255,255,255,0.65)");
  gr.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
});

// A pool of light — the table floats on these.
const hazeTex = () => makeTex("atmos-haze", 256, 256, (g, w, h) => {
  const gr = g.createRadialGradient(128, 128, 0, 128, 128, 128);
  gr.addColorStop(0, "rgba(255,255,255,0.42)");
  gr.addColorStop(0.35, "rgba(255,255,255,0.16)");
  gr.addColorStop(0.72, "rgba(255,255,255,0.04)");
  gr.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = gr; g.fillRect(0, 0, w, h);
});

// ═══════════════════════════════════════════════════════════════════
// 1 · THE HORIZON — two half-cylinders of aenergy standing around the
// duel. Warm ember gold rises from your half, cold crimson breathes
// down from theirs — the 2D Veil's ribbon law, made a place you're
// standing inside of. The active seat's half brightens on its turn,
// and yours goes bloody as your Anima falls.
//
// The half-split is geometric, not a texture trick: CylinderGeometry
// walks theta from +Z, so thetaStart −π/2 sweeps exactly the +Z half
// (mine) and +π/2 exactly the −Z half (theirs). No UV alignment to get
// wrong, no rotation constant to tune.
// ═══════════════════════════════════════════════════════════════════
const HORIZON = { rBot: 15.0, rTop: 18.6, h: 8.4, y: 2.6, seg: 40 };

const HorizonHalf = memo(function HorizonHalf({ isMe }) {
  const mat = useRef();
  const warm = useMemo(() => new THREE.Color(SD.gold), []);
  const cold = useMemo(() => new THREE.Color("#7a1230"), []);
  const blood = useMemo(() => new THREE.Color(SD.redMist), []);
  const scratch = useMemo(() => new THREE.Color(), []);
  const map = useMemo(() => veilTex(isMe ? "horizon-m" : "horizon-o"), [isMe]);

  useFrame((st, dt) => {
    const m = mat.current;
    if (!m) return;
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const active = ATMOS.myTurn === !!isMe;
    const w = woundOf(isMe);

    // The seat's own colour, bled toward crimson by its own wounds.
    scratch.copy(isMe ? warm : cold).lerp(blood, isMe ? w * 0.85 : w * 0.5);
    m.color.lerp(scratch, 1 - Math.exp(-2.2 * step));

    // Breath + turn dominance + a swell right after the chair changes.
    const breath = 0.78 + Math.sin(t * 0.42 + (isMe ? 0 : 2.1)) * 0.22;
    const since = (Date.now() - ATMOS.turnAt) / 900;
    const swell = active && since < 1 ? (1 - since) * 0.5 : 0;
    const goal = (active ? 0.30 : 0.13) * breath + swell + w * 0.16;
    m.opacity = approach(m.opacity, goal, 3.4, step);

    // Slow drift so the curtain never sits still.
    map.offset.x = (map.offset.x + step * (isMe ? 0.012 : -0.009)) % 1;
    map.offset.y = Math.sin(t * 0.13) * 0.03;
  });

  return (
    <mesh position={[0, HORIZON.y, 0]} renderOrder={-4}>
      <cylinderGeometry args={[
        HORIZON.rTop, HORIZON.rBot, HORIZON.h, HORIZON.seg, 1, true,
        isMe ? -Math.PI / 2 : Math.PI / 2, Math.PI,
      ]} />
      <meshBasicMaterial ref={mat} map={map} color={SD.gold}
        transparent opacity={0.2} side={THREE.BackSide}
        depthWrite={false} blending={THREE.AdditiveBlending}
        toneMapped={false} fog={false} />
    </mesh>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 2 · THE UNDERLIGHT — three soft pools beneath the plate. The table
// stops being a plane sitting in nothing and starts FLOATING. Cheap
// (three discs), and it is most of why the void reads as deep.
// ═══════════════════════════════════════════════════════════════════
const UnderLight = memo(function UnderLight() {
  const a = useRef(); const b = useRef(); const c = useRef();
  const map = useMemo(() => hazeTex(), []);
  useFrame((st) => {
    const t = st.clock.elapsedTime;
    if (a.current) { a.current.rotation.z = t * 0.05; a.current.material.opacity = 0.22 + Math.sin(t * 0.5) * 0.05; }
    if (b.current) { b.current.rotation.z = -t * 0.032; b.current.material.opacity = 0.15 + Math.sin(t * 0.37 + 1.7) * 0.045; }
    if (c.current) { c.current.rotation.z = t * 0.019; }
  });
  const disc = (ref, size, y, color, op) => (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]} renderOrder={-3}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial map={map} color={color} transparent opacity={op}
        depthWrite={false} blending={THREE.AdditiveBlending}
        toneMapped={false} fog={false} />
    </mesh>
  );
  return (
    <group>
      {disc(a, 26, -0.55, SD.purple, 0.22)}
      {disc(b, 19, -1.45, SD.purpleDim, 0.15)}
      {disc(c, 33, -2.90, SD.void4, 0.30)}
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 3 · THE SEAM — the line at z=0 is not a hairline any more, it is a
// TEAR. A lit crack across the plate, a low wall of light standing out
// of it, and a flare on every strike. This is the Shatterfield's own
// name made visible.
// ═══════════════════════════════════════════════════════════════════
const surgeCol = new THREE.Color();
const seamCol = new THREE.Color(SD.goldBright);
const SeamRift = memo(function SeamRift() {
  const crack = useRef(); const wallA = useRef(); const wallB = useRef();
  const cmap = useMemo(() => crackTex(), []);
  const vmap = useMemo(() => veilTex("seam"), []);
  useFrame((st, dt) => {
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const heat = clamp01((Date.now() - ATMOS.strikeAt) / 700);
    const flare = (1 - heat) * (1 - heat);         // quadratic decay
    const pulse = 0.55 + Math.sin(t * 1.15) * 0.16;
    // H24 — the tear takes the surge's colour, not just its own gold.
    // The seam is the one thing visible from every camera preset, so a
    // score washing it is the cheapest possible way to make the whole
    // table feel the moment.
    const sg = surgeLevel();
    if (sg > 0.001 && crack.current) {
      surgeCol.set(ATMOS.surgeHue);
      crack.current.material.color.lerp(surgeCol, Math.min(0.6, sg));
    } else if (crack.current) {
      crack.current.material.color.lerp(seamCol, 0.06);
    }
    if (crack.current) crack.current.material.opacity = pulse * 0.62 + flare * 0.9 + sg * 0.8;
    if (wallA.current) {
      wallA.current.material.opacity = 0.085 * pulse + flare * 0.26 + sg * 0.4;
      vmap.offset.y = (vmap.offset.y + step * 0.11) % 1;
    }
    if (wallB.current) wallB.current.material.opacity = 0.055 * pulse + flare * 0.18 + sg * 0.26;
  });
  const wall = (ref, ry, op) => (
    <mesh ref={ref} rotation={[0, ry, 0]} position={[0, 0.62, 0]} renderOrder={-1}>
      <planeGeometry args={[TABLE.W * 0.94, 1.24]} />
      <meshBasicMaterial map={vmap} color={SD.goldBright} transparent opacity={op}
        depthWrite={false} side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
    </mesh>
  );
  return (
    <group>
      <mesh ref={crack} rotation={[-Math.PI / 2, 0, 0]}
        position={[0, Y.seam + 0.0015, 0]} renderOrder={-1}>
        <planeGeometry args={[TABLE.W * 1.02, 1.05]} />
        <meshBasicMaterial map={cmap} color={SD.goldBright} transparent opacity={0.6}
          depthWrite={false} blending={THREE.AdditiveBlending}
          toneMapped={false} fog={false} />
      </mesh>
      {wall(wallA, 0, 0.085)}
      {wall(wallB, 0.16, 0.055)}
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 4 · THE WELLS — the two vortices, given bodies in the world.
// ═══════════════════════════════════════════════════════════════════
// Seat-local anchors. Written for MY seat; the opposing seat renders
// inside <group rotation={[0, π, 0]}> and mirrors for free — the same
// one-layout-two-chairs trick fieldLayout3D uses. All DIALS.
const WELL = {
  anima: [-6.30, 1.38, 2.30],
  khao: [6.30, 1.38, 2.30],
};

// ── THE KHAO WELL — a funnel of violet light, drinking. Spins faster
//    and burns brighter the fuller your Khao is; when you're empty it
//    guts out to a dim eye. ───────────────────────────────────────────
const KhaoWell = memo(function KhaoWell({ isMe }) {
  const discA = useRef(); const discB = useRef(); const throat = useRef();
  const core = useRef(); const lamp = useRef(); const grp = useRef();
  const spiral = useMemo(() => spiralTex(), []);
  const wall = useMemo(() => veilTex(isMe ? "khao-m" : "khao-o"), [isMe]);
  const spin = useRef(0);

  useFrame((st, dt) => {
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const k = khaoOf(isMe);                       // 0 empty → 1 full
    spin.current += step * (0.35 + k * 1.55);

    if (grp.current) grp.current.position.y = WELL.khao[1] + Math.sin(t * 0.75) * 0.045;
    if (discA.current) {
      discA.current.rotation.z = spin.current;
      discA.current.material.opacity = approach(discA.current.material.opacity, 0.22 + k * 0.52, 3, step);
    }
    if (discB.current) {
      discB.current.rotation.z = -spin.current * 0.62;
      discB.current.scale.setScalar(0.72 + Math.sin(t * 1.1) * 0.03);
      discB.current.material.opacity = approach(discB.current.material.opacity, 0.14 + k * 0.40, 3, step);
    }
    if (throat.current) {
      throat.current.rotation.y = -spin.current * 0.8;
      wall.offset.y = (wall.offset.y - step * (0.25 + k * 0.9)) % 1;   // falling in
      throat.current.material.opacity = approach(throat.current.material.opacity, 0.16 + k * 0.34, 3, step);
    }
    if (core.current) {
      const s = 0.9 + Math.sin(t * 2.3) * 0.08 + k * 0.18;
      core.current.scale.setScalar(s);
      core.current.material.emissiveIntensity = approach(
        core.current.material.emissiveIntensity, 0.5 + k * 2.4, 3, step);
    }
    if (lamp.current) lamp.current.intensity = approach(lamp.current.intensity, 1.6 + k * 7.5, 3, step);
  });

  const face = (ref, r, op) => (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]} renderOrder={2}>
      <planeGeometry args={[r, r]} />
      <meshBasicMaterial map={spiral} color={SD.purpleLight} transparent opacity={op}
        depthWrite={false} blending={THREE.AdditiveBlending}
        toneMapped={false} fog={false} />
    </mesh>
  );

  return (
    <group ref={grp} position={WELL.khao}>
      {face(discA, 2.05, 0.4)}
      {face(discB, 1.45, 0.28)}
      {/* the throat — matter falling toward the eye */}
      <mesh ref={throat} position={[0, -0.62, 0]} renderOrder={1}>
        <cylinderGeometry args={[0.92, 0.17, 1.24, 26, 1, true]} />
        <meshBasicMaterial map={wall} color={SD.purple} transparent opacity={0.3}
          side={THREE.DoubleSide} depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
      </mesh>
      {/* the eye */}
      <mesh ref={core} position={[0, -1.16, 0]}>
        <icosahedronGeometry args={[0.17, 1]} />
        <meshStandardMaterial color="#1a0533" emissive={SD.purpleLight}
          emissiveIntensity={1.6} roughness={0.25} metalness={0.5}
          toneMapped={false} />
      </mesh>
      {!LOW && (
        <pointLight ref={lamp} position={[0, -0.7, 0]} color={SD.purpleLight}
          intensity={5} distance={6.5} decay={2} />
      )}
    </group>
  );
});

// ── THE ANIMA WELL — a crystal heart on two orbits, weeping. It does
//    not spin faster when you're winning; it CRACKS OPEN when you're
//    losing, and what pours out of it is the mist on your half of the
//    field. Same lore as the 2D Rent Sun's wound-cracks. ─────────────
const AnimaWell = memo(function AnimaWell({ isMe }) {
  const grp = useRef(); const heart = useRef();
  const ringA = useRef(); const ringB = useRef();
  const fall = useRef(); const lamp = useRef();
  const mist = useMemo(() => mistTex(), []);
  const calm = useMemo(() => new THREE.Color(SD.red), []);
  const dire = useMemo(() => new THREE.Color("#ff5a6e"), []);
  const scratch = useMemo(() => new THREE.Color(), []);

  useFrame((st, dt) => {
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const w = woundOf(isMe);
    const beat = Math.pow(Math.max(0, Math.sin(t * (1.15 + w * 1.5))), 6); // a HEARTbeat

    if (grp.current) grp.current.position.y = WELL.anima[1] + Math.sin(t * 0.62 + 1.3) * 0.04;
    if (heart.current) {
      heart.current.rotation.y = t * 0.30;
      heart.current.rotation.x = Math.sin(t * 0.4) * 0.16;
      const s = 0.94 + beat * 0.16;
      heart.current.scale.set(s, s * 1.18, s);
      const m = heart.current.material;
      scratch.copy(calm).lerp(dire, w);
      m.emissive.lerp(scratch, 1 - Math.exp(-2.5 * step));
      m.emissiveIntensity = approach(m.emissiveIntensity, 0.85 + w * 1.3 + beat * 1.1, 6, step);
    }
    if (ringA.current) { ringA.current.rotation.z = t * 0.55; ringA.current.rotation.x = Math.PI / 2.4; }
    if (ringB.current) { ringB.current.rotation.y = -t * 0.42; ringB.current.rotation.z = Math.PI / 3.1; }
    if (fall.current) {
      fall.current.rotation.y = t * 0.14;
      fall.current.material.opacity = approach(fall.current.material.opacity, 0.10 + w * 0.42, 2.4, step);
      const s = 1 + w * 0.35;
      fall.current.scale.set(s, 1, s);
    }
    if (lamp.current) lamp.current.intensity = approach(lamp.current.intensity, 2.2 + w * 6 + beat * 2.5, 6, step);
  });

  return (
    <group ref={grp} position={WELL.anima}>
      <mesh ref={heart}>
        <icosahedronGeometry args={[0.30, 0]} />
        <meshStandardMaterial color="#2a0510" emissive={SD.red} emissiveIntensity={1}
          roughness={0.22} metalness={0.65} flatShading toneMapped={false} />
      </mesh>
      <mesh ref={ringA}>
        <torusGeometry args={[0.60, 0.014, 6, 60]} />
        <meshBasicMaterial color={SD.red} transparent opacity={0.55}
          depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
      </mesh>
      <mesh ref={ringB}>
        <torusGeometry args={[0.78, 0.010, 6, 60]} />
        <meshBasicMaterial color="#ff8294" transparent opacity={0.35}
          depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
      </mesh>
      {/* what it weeps — a cone of mist falling to the plate */}
      <mesh ref={fall} position={[0, -0.78, 0]} renderOrder={1}>
        <cylinderGeometry args={[0.34, 1.85, 1.5, 22, 1, true]} />
        <meshBasicMaterial map={mist} color={SD.redMist} transparent opacity={0.18}
          side={THREE.DoubleSide} depthWrite={false}
          blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
      </mesh>
      {!LOW && (
        <pointLight ref={lamp} position={[0, -0.5, 0]} color={SD.red}
          intensity={3} distance={6} decay={2} />
      )}
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 5 · THE KHAO RIVERS — light running the perimeter out of the seam
// and into the well. Two lanes per seat: a tube carrying a scrolling
// rail texture, plus loose sparks riding the same curve so the flow
// reads as MOVING and not merely as a lit pipe.
//
// Every control point is a DIAL. They hug the outer wing and the seam,
// clear of the rift rows, the piles and the hand (the law of this ship).
// ═══════════════════════════════════════════════════════════════════
const RIVER_LANES = [
  [[0.00, 0.055, 0.30], [2.55, 0.065, 0.60], [4.85, 0.085, 1.20], [6.00, 0.45, 1.90], [6.28, 1.10, 2.24]],
  [[0.10, 0.050, 0.02], [3.35, 0.060, 0.14], [5.70, 0.100, 0.62], [6.35, 0.60, 1.35], [6.34, 1.20, 2.12]],
];
const SPARKS_PER_LANE = LOW ? 12 : 22;

const KhaoRiver = memo(function KhaoRiver({ isMe, lane }) {
  const tube = useRef(); const pts = useRef();
  const key = `${isMe ? "m" : "o"}${lane}`;
  const curve = useMemo(
    () => new THREE.CatmullRomCurve3(RIVER_LANES[lane].map((p) => new THREE.Vector3(...p))),
    [lane]
  );
  const geo = useMemo(() => new THREE.TubeGeometry(curve, 96, 0.034, 6, false), [curve]);
  const map = useMemo(() => riverTex(key), [key]);
  const sparkMap = useMemo(() => sparkTex(), []);

  // Sparks: one seeded phase each, marched along the same curve.
  const sparkGeo = useMemo(() => {
    const R = rnd(`river-sparks|${key}`);
    const pos = new Float32Array(SPARKS_PER_LANE * 3);
    const ph = new Float32Array(SPARKS_PER_LANE);
    for (let i = 0; i < SPARKS_PER_LANE; i++) ph[i] = R();
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.userData.ph = ph;
    return g;
  }, [key]);

  useEffect(() => () => { geo.dispose(); sparkGeo.dispose(); }, [geo, sparkGeo]);

  const v = useMemo(() => new THREE.Vector3(), []);
  useFrame((_, dt) => {
    const step = Math.min(dt, 0.05);
    const k = khaoOf(isMe);
    const speed = 0.16 + k * 0.42;

    if (tube.current) {
      map.repeat.x = 5;
      map.offset.x = (map.offset.x - step * (speed * 3.4)) % 1;
      tube.current.material.opacity = approach(tube.current.material.opacity, 0.20 + k * 0.62, 3, step);
    }
    if (pts.current) {
      const arr = sparkGeo.attributes.position.array;
      const ph = sparkGeo.userData.ph;
      for (let i = 0; i < SPARKS_PER_LANE; i++) {
        ph[i] = (ph[i] + step * speed) % 1;
        curve.getPointAt(ph[i], v);
        arr[i * 3] = v.x; arr[i * 3 + 1] = v.y + 0.02; arr[i * 3 + 2] = v.z;
      }
      sparkGeo.attributes.position.needsUpdate = true;
      pts.current.material.opacity = approach(pts.current.material.opacity, 0.25 + k * 0.65, 3, step);
    }
  });

  return (
    <group>
      <mesh ref={tube} geometry={geo} renderOrder={1}>
        <meshBasicMaterial map={map} color={SD.purpleLight} transparent opacity={0.4}
          depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
      </mesh>
      <points ref={pts} geometry={sparkGeo} renderOrder={2}>
        <pointsMaterial map={sparkMap} size={0.115} color="#d8bcff" transparent opacity={0.6}
          sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending}
          toneMapped={false} fog={false} />
      </points>
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 6 · THE ANIMA MIST — banks of crimson fog standing off the outer rim
// of each seat. At full Anima they sit out in the dark, barely there.
// As a seat bleeds they THICKEN and CREEP INWARD, until a player one
// hit from death is playing inside a red fog bank.
//
// This is the 3D answer to the 2D Solrend Mirror: the world tells you
// the score without a single number. Legibility is safe either way —
// the real card faces are DOM on the CSS3D layer, which composites
// ABOVE the WebGL canvas, so mist can never make a card unreadable.
// ═══════════════════════════════════════════════════════════════════
const MIST_N = LOW ? 6 : 9;

const AnimaMist = memo(function AnimaMist({ isMe }) {
  const grp = useRef();
  const map = useMemo(() => mistTex(), []);
  const banks = useMemo(() => {
    const R = rnd(`anima-mist|${isMe ? "m" : "o"}`);
    return Array.from({ length: MIST_N }, (_, i) => {
      const a = (i / (MIST_N - 1)) * Math.PI;             // sweep MY half only
      return {
        x: Math.cos(a) * (7.4 + R() * 0.9),
        z: Math.sin(a) * (5.0 + R() * 0.8) + 0.15,
        y: 0.030 + i * 0.013,                              // z-fight law: unique Y
        rot: R() * Math.PI * 2,
        spin: (R() - 0.5) * 0.055,
        size: 4.4 + R() * 2.6,
        base: 0.16 + R() * 0.12,
        drift: 0.25 + R() * 0.5,
      };
    });
  }, [isMe]);

  useFrame((st, dt) => {
    const g = grp.current;
    if (!g) return;
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const w = woundOf(isMe);

    // Creep: the whole ring walks toward the seam as the seat bleeds…
    const pull = 1 - w * 0.30;
    g.scale.set(pull, 1, pull);

    g.children.forEach((m, i) => {
      const b = banks[i];
      if (!b) return;
      m.rotation.z = b.rot + t * b.spin;                   // …and each bank
      m.position.y = b.y + Math.sin(t * b.drift + i) * 0.02;
      const s = 1 + w * 0.55;                              // …swells.
      m.scale.set(s, s, 1);
      m.material.opacity = approach(
        m.material.opacity, b.base * (0.22 + w * 2.3), 1.8, step);
    });
  });

  return (
    <group ref={grp}>
      {banks.map((b, i) => (
        <mesh key={i} rotation={[-Math.PI / 2, 0, b.rot]}
          position={[b.x, b.y, b.z]} renderOrder={0}>
          <planeGeometry args={[b.size, b.size]} />
          <meshBasicMaterial map={map} color={SD.redMist} transparent opacity={0.05}
            depthWrite={false} blending={THREE.AdditiveBlending}
            toneMapped={false} fog={false} />
        </mesh>
      ))}
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// 7 · THE DRIFT — tinted particulate. Crimson ash falls on a wounded
// seat; violet khao-sparks rise off the rivers. (Parent's <Motes/>
// keeps owning the gold embers — one system per colour, no duplicates.)
// ═══════════════════════════════════════════════════════════════════
const Drift = memo(function Drift({ seat, tint, n, rise, tone }) {
  const pts = useRef();
  const sprite = useMemo(() => sparkTex(), []);
  const geo = useMemo(() => {
    const R = rnd(`drift|${seat}|${tone}`);
    const pos = new Float32Array(n * 3);
    const spd = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      pos[i * 3] = (R() - 0.5) * TABLE.W * 1.35;
      pos[i * 3 + 1] = R() * 3.4;
      pos[i * 3 + 2] = (R() * 0.5 + 0.02) * TABLE.D * 1.1;   // my half only
      spd[i] = 0.08 + R() * 0.24;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    g.userData.spd = spd;
    return g;
  }, [seat, tone, n]);
  useEffect(() => () => geo.dispose(), [geo]);

  useFrame((st, dt) => {
    const p = geo.attributes.position.array;
    const spd = geo.userData.spd;
    const step = Math.min(dt, 0.05);
    const t = st.clock.elapsedTime;
    const isMe = seat === "m";
    const drive = tone === "ash" ? woundOf(isMe) : khaoOf(isMe);
    for (let i = 0; i < n; i++) {
      p[i * 3 + 1] += spd[i] * step * rise;
      p[i * 3] += Math.sin(t * 0.5 + i) * 0.0007;
      if (rise > 0 && p[i * 3 + 1] > 3.6) p[i * 3 + 1] = 0.04;
      if (rise < 0 && p[i * 3 + 1] < 0.04) p[i * 3 + 1] = 3.6;
    }
    geo.attributes.position.needsUpdate = true;
    if (pts.current) {
      pts.current.material.opacity = approach(
        pts.current.material.opacity, 0.06 + drive * 0.42, 2, step);
    }
  });

  return (
    <points ref={pts} geometry={geo} renderOrder={1}>
      <pointsMaterial map={sprite} size={0.07} color={tint} transparent opacity={0.1}
        sizeAttenuation depthWrite={false} blending={THREE.AdditiveBlending}
        toneMapped={false} fog={false} />
    </points>
  );
});

// ═══════════════════════════════════════════════════════════════════
// THE SURGE WASH (H24) — the field answers a score
// ═══════════════════════════════════════════════════════════════════
// One plane, the size of the table, lying just above the felt and
// invisible for the entire duel except in the ~900ms after a score
// declares a surge. It is the atmosphere's half of the biggest moments
// in the game: Show Stomper does not merely crack the ground, the whole
// room goes red for a beat.
//
// ── WHY A PLANE ON THE FELT AND NOT A FULL-SCREEN FLASH ────────────
// A DOM flash over the canvas is cheaper and it is what most games do,
// and it is wrong here for a specific reason: THE ATMOSPHERE FRAMES THE
// BOARD, NEVER COVERS IT (this ship's own law, §16). A screen wash
// dims cards, and a card you cannot read for 900ms during a Response
// Window is a rules problem wearing a visual costume.
//
// On the felt, under the cards, additive: the table lights up and every
// card stays exactly as legible as it was.
//
// ── AND WHY IT COSTS NOTHING WHEN IDLE ─────────────────────────────
// `visible = false` the instant the level hits zero, so it is not in
// the render list at all for 99% of a duel. It reads one module-level
// number per frame and writes two material properties. There is no
// state, no subscription, and nothing to reconcile.
const SurgeWash = memo(function SurgeWash() {
  const ref = useRef();
  const map = useMemo(() => veilTex("surge"), []);
  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const sg = surgeLevel();
    if (sg <= 0.001) { m.visible = false; return; }
    m.visible = true;
    surgeCol.set(ATMOS.surgeHue);
    m.material.color.copy(surgeCol);
    m.material.opacity = sg * 0.30;
    // A hair of lift and spread as it blooms, so it reads as light
    // filling the room rather than as a coloured rectangle switching on.
    const k = 1 + sg * 0.06;
    m.scale.set(k, k, 1);
  });
  return (
    <mesh ref={ref} rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0.03, 0]} renderOrder={-2} visible={false}>
      <planeGeometry args={[TABLE.W * 1.06, TABLE.D * 1.06]} />
      <meshBasicMaterial map={map} transparent opacity={0} depthWrite={false}
        blending={THREE.AdditiveBlending} toneMapped={false} fog={false} />
    </mesh>
  );
});

// ═══════════════════════════════════════════════════════════════════
// ONE SEAT'S WEATHER — authored in MY coordinates, mirrored for the
// adversary by the same π-about-Y group the board itself uses.
// ═══════════════════════════════════════════════════════════════════
const SeatWeather = memo(function SeatWeather({ isMe }) {
  return (
    <group rotation={[0, isMe ? 0 : Math.PI, 0]}>
      <AnimaMist isMe={isMe} />
      <KhaoRiver isMe={isMe} lane={0} />
      <KhaoRiver isMe={isMe} lane={1} />
      <AnimaWell isMe={isMe} />
      <KhaoWell isMe={isMe} />
      <Drift seat={isMe ? "m" : "o"} tone="ash" tint={SD.redMist} n={LOW ? 26 : 44} rise={-1} />
      <Drift seat={isMe ? "m" : "o"} tone="khao" tint={SD.purpleLight} n={LOW ? 22 : 38} rise={1} />
    </group>
  );
});

// ═══════════════════════════════════════════════════════════════════
// THE WRAPPER — the ONLY thing in this file that touches props.
// It writes the bus and renders a subtree of memo'd children that take
// nothing but a stable boolean, so a Firebase tick reconciles ZERO of
// the atmosphere. Mount it once, inside <WorldShake>.
// ═══════════════════════════════════════════════════════════════════
export default memo(function FieldAtmos3D({
  myAnima = 30, oppAnima = 30, maxAnima = 30,
  myKhao = 3, myMaxKhao = 3, oppKhao = 3, oppMaxKhao = 3,
  myTurn = true,
}) {
  const prev = useRef({ my: myAnima, opp: oppAnima });

  useEffect(() => {
    ATMOS.maxAnima = Math.max(1, maxAnima);
    ATMOS.myAnima = myAnima; ATMOS.oppAnima = oppAnima;
    ATMOS.myKhao = myKhao; ATMOS.myMaxKhao = myMaxKhao;
    ATMOS.oppKhao = oppKhao; ATMOS.oppMaxKhao = oppMaxKhao;
    // A strike on EITHER seat flares the seam — the tear reacts to
    // violence, not to allegiance.
    if (myAnima < prev.current.my || oppAnima < prev.current.opp) ATMOS.strikeAt = Date.now();
    prev.current = { my: myAnima, opp: oppAnima };
  }, [myAnima, oppAnima, maxAnima, myKhao, myMaxKhao, oppKhao, oppMaxKhao]);

  useEffect(() => { ATMOS.myTurn = !!myTurn; ATMOS.turnAt = Date.now(); }, [myTurn]);

  return (
    <group>
      <HorizonHalf isMe />
      <HorizonHalf isMe={false} />
      <UnderLight />
      <SeamRift />
      <SurgeWash />
      <SeatWeather isMe />
      <SeatWeather isMe={false} />
    </group>
  );
});
